<template>
  <div class="min-h-screen  pb-10">
    <PageTitle title="Ventes" :description="`${total} vente(s) au total`">
      <!-- <template #actions>
        <router-link :to="{ name: 'sale-simple' }">
          <BaseButton variant="primary">
            <template #icon-left>
              <i class="fa-solid fa-plus"></i>
            </template>
            Nouvelle vente
          </BaseButton>
        </router-link>
      </template> -->
    </PageTitle>

    <!-- Filtres -->
    <BaseCard class="mb-6">
      <div class="flex flex-wrap gap-4">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput v-model="search" type="search" placeholder="Rechercher une vente..."
            icon-left='<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>'
            clearable @input="handleSearch" />
        </div>

        <!-- Filtres de date -->
        <div class="flex gap-3">
          <div class="flex flex-col gap-1">
            <label for="dateFrom" class="text-sm font-medium text-gray-600">Du</label>
            <BaseInput id="dateFrom" v-model="filters.dateFrom" type="date" @change="loadSales" />
          </div>
          <div class="flex flex-col gap-1">
            <label for="dateTo" class="text-sm font-medium text-gray-600">Au</label>
            <BaseInput id="dateTo" v-model="filters.dateTo" type="date" @change="loadSales" />
          </div>
        </div>

        <!-- Filtre de type -->
        <div class="flex flex-col gap-1">
          <label for="typeFilter" class="text-sm font-medium text-gray-600">Type</label>
          <select id="typeFilter" v-model="filters.type" @change="loadSales"
            class="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[180px]">
            <option value="">Tous les statuts</option>
            <option value="single">Vente simple</option>
            <option value="deferred">Vente différée</option>
          </select>
        </div>
      </div>
    </BaseCard>

    <!-- Tableau des ventes -->
    <BaseCard>
      <!-- État de chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement des ventes...</p>
      </div>

      <!-- État vide -->
      <div v-else-if="sales.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 text-gray-300 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Aucune vente trouvée</h3>
        <p class="text-gray-600 max-w-md mb-6">
          Cliquez sur "Nouvelle vente" pour commencer à enregistrer des ventes.
        </p>
        <router-link :to="{ name: 'sale-simple' }">
          <BaseButton variant="primary" size="lg">
            <template #icon-left>
              <i class="fa-solid fa-plus"></i>
            </template>
            Commencer à vendre
          </BaseButton>
        </router-link>
      </div>

      <!-- Tableau -->
      <div v-else class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50 border-b-2 border-gray-200">
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                N° Facture
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Date
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Montant
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Client
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Paiement
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Vendeur
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Statut
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="sale in sales" :key="sale.id" :class="[
              'hover:bg-gray-50 transition-colors',
              { 'opacity-60 bg-gray-50': sale.status === 'refunded' }
            ]">
              <td class="px-4 py-4">
                <span class="font-mono font-semibold text-gray-900">{{ sale.invoice_number }}</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                {{ formatDate(sale.sale_date) }}
              </td>
              <td class="px-4 py-4">
                <span class="font-semibold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                <span v-if="sale.customer && sale.customer.name" class="text-gray-900 font-medium">
                  {{ sale.customer.name }}
                </span>
                <span v-else class="text-gray-400 italic">-</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                {{ sale.payment_label || 'N/A' }}
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                {{ sale.user ? sale.user.name : 'N/A' }}
              </td>
              <td class="px-4 py-4">
                <SaleStatusBadge :sale="sale" />
              </td>
              <td class="px-4 py-4">
                <div class="flex items-center justify-end gap-2">
                  <BaseButton variant="danger" size="sm" :loading="sale.loading" @click="downloadInvoice(sale)">
                    <i class="fa-solid fa-file-pdf"></i>
                  </BaseButton>
                  <router-link :to="{ name: 'sale-details', params: { id: sale.id } }">
                    <BaseButton variant="outline-primary" size="xs">
                      Voir détails
                      <i class="fa-solid fa-arrow-right ms-2"></i>
                    </BaseButton>
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="sales.length > 0" class="border-t border-gray-200 px-4 py-4">
        <PaginationComponent :current-page="currentPage" :per-page="perPage" :total="total" :to="paginationInfo.to"
          :show-page-numbers="true" :max-visible-pages="5" @page-change="goToPage" />
      </div>
    </BaseCard>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { salesService } from '@/services'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue'

export default defineComponent({
  name: 'SalesView',
  components: {
    PageTitle,
    BaseCard,
    BaseButton,
    BaseInput,
    PaginationComponent,
    SaleStatusBadge
  },
  setup() {
    // Données d'état
    const sales = ref([])
    const loading = ref(true)
    const isDownloading = ref(false)
    const search = ref('')
    const filters = reactive({
      dateFrom: '',
      dateTo: '',
      type: ''
    })

    // Pagination
    const currentPage = ref(1)
    const lastPage = ref(1)
    const perPage = ref(10)
    const total = ref(0)
    const paginationTo = ref(0)
    const paginationFrom = ref(0)
    const searchTimeout = ref(null)

    // Résumé des ventes
    const salesCount = ref(0)
    const totalAmount = ref(0)
    const averageSaleAmount = ref(0)

    // Calcul des informations de pagination
    const paginationInfo = computed(() => {
      return {
        from: paginationFrom.value,
        to: paginationTo.value,
        total: total.value
      }
    })

    // Méthodes
    const loadSales = async () => {
      try {
        loading.value = true

        // Préparer les paramètres
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          with: ['customer', 'user']
        }

        // Ajouter les filtres
        if (search.value) {
          params.search = search.value
        }
        if (filters.dateFrom) {
          params.date_from = filters.dateFrom
        }
        if (filters.dateTo) {
          params.date_to = filters.dateTo
        }
        if (filters.type) {
          params.type = filters.type
        }

        // Appel à l'API
        const response = await salesService.getSales(params)

        // Structure: { data: [...], meta: { total, per_page, from, to } }
        sales.value = response.data?.data || response.data || []
        sales.value.forEach(sale => {
          sale.loading = false
        })

        // Mettre à jour les données de pagination depuis meta
        const meta = response.data?.meta || response.meta || {}
        total.value = meta.total || 0
        perPage.value = meta.per_page || 10
        paginationTo.value = meta.to || 0
        paginationFrom.value = meta.from || 0
        lastPage.value = Math.ceil(total.value / perPage.value)

      } catch (error) {
        console.error('Erreur lors du chargement des ventes:', error)
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        loadSales()
      }, 500)
    }

    const goToPage = (page) => {
      if (page < 1 || page > lastPage.value) return
      currentPage.value = page
      loadSales()
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''

      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }



    // Méthodes d'action
    const downloadInvoice = async (sale) => {
      if (sale.loading) return
      sale.loading = true
      try {
        isDownloading.value = true
        await salesService.downloadInvoice(sale.id, true)
        isDownloading.value = false
      } catch (err) {
        isDownloading.value = false
        console.error('Erreur lors du téléchargement de la facture:', err)
      } finally {
        sale.loading = false
      }
    }

    // Nettoyer le timeout lors du démontage
    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    // Charger les données au montage du composant
    onMounted(() => {
      loadSales()
    })

    return {
      sales,
      loading,
      search,
      filters,
      currentPage,
      lastPage,
      perPage,
      total,
      salesCount,
      totalAmount,
      averageSaleAmount,
      paginationInfo,
      isDownloading,
      downloadInvoice,
      loadSales,
      handleSearch,
      goToPage,
      formatCurrency,
      formatDate
    }
  }
})
</script>
